'use client'

import React, {ReactNode, useEffect} from 'react'
import {useSelector} from 'react-redux'
import {Router} from 'react-router-dom'
import {
  DeviceLayoutProvider,
  deviceTypeToBreakpoint
} from '@vio/atlas/context/deviceLayout'
import {ToastProvider} from '@vio/atlas/context/toastController'
import FontsLoader from '@vio/atlas/helpers/FontsLoader'
import {DebugPanelModules, DebugPanelProvider} from '@vio/debug-panel'
import {getPersistedSimulationFlag} from '@vio/debug-panel/src/utils/scenarioSimulator'
import {EmailSignUpProvider} from '@vio/shared/src/authentication/Email/EmailSignUpProvider'
import {EmailFlow} from '@vio/shared/src/authentication/Email/index'
import {useSUSIBottomSheet} from '@vio/shared/src/authentication/hooks/useSUSIBottomSheet'
import {OneTimePasswordFlow} from '@vio/shared/src/authentication/OneTimePassword/index'
import {SusiProvider} from '@vio/shared/src/authentication/SUSI/bottomSheet/SusiBottomSheet/SusiProvider'
import {PasswordlessLogin} from '@vio/shared/src/authentication/SUSI/overlay'
import {DealFreeze} from '@vio/shared/src/dealFreeze'
import {ApplicationNames} from '@vio/shared/src/dealFreeze/modules/slice'
import {SyncStateWithPriceWatchHoldoutGroup} from '@vio/shared/src/priceWatch/components/SyncStateWithPriceWatchHoldoutGroup'
import PerformanceMonitor from '@vio/shared/src/utils/PerformanceMonitor/PerformanceMonitor'
import {TmsPanelProvider} from '@vio/tms'
import {CaptureVioClickID} from 'components/CaptureVioClickID'
import SyncStateWithDeviceLayout from 'components/data/SyncStateWithDeviceLayout'
import {useUrlUpdater} from 'components/data/WithUrlUpdater'
import {ErrorBoundaryWrapper as ErrorBoundary} from 'components/ErrorBoundary'
import InitializeSentry from 'components/InitializeSentry'
import {PreferencesOverlay} from 'components/PreferencesOverlay'
import {PriceWatchOverlays} from 'components/PriceWatch/components/PriceWatchOverlays'
import {PriceWatchTrackingsSync} from 'components/PriceWatch/components/PriceWatchTrackingsSync'
import InjectPageHeadTags from 'components/Seo/InjectPageHeadTags'
import InjectPageMetaDescription from 'components/Seo/InjectPageMetaDescription'
import {TrackGoogleTranslateUsage} from 'components/TrackGoogleTranslateUsage/TrackGoogleTranslateUsage'
import {TrackSSRExperiments} from 'components/TrackSSRExperiments/TrackSSRExperiments'
import {Web2AppPopup} from 'components/Web2AppPopup/Web2AppPopup'
import {usePageLoadStartTimer} from 'hooks/usePageLoad'
import {useSyncOverlayState} from 'hooks/useSyncOverlayState'
import {scenariosList} from 'mocks/scenarios'
import {setScreenshotTaken} from 'modules/analytics/slice'
import {
  getAnonymousId,
  getCurrencyCode,
  getDeviceLayout,
  getIsSyntheticTest,
  getLanguageCode,
  getUserCountryCode
} from 'modules/meta/selectors'
import {useRouter} from 'next/router'
import pathToRegexp from 'path-to-regexp'
import {history} from 'utils'

import {useBrand} from '@daedalus/core/src/_web/brand/hooks/useBrand'
import {RouterProvider} from '@daedalus/core/src/_web/router/context/RouterProvider'
import {RouterProvider as RouterProviderNext} from '@daedalus/core/src/_web/router/context/RouterProviderNext'
import {getQueryStringObject} from '@daedalus/core/src/_web/utils/url'
import {FetchAclDecision} from '@daedalus/core/src/acl/components/FetchAclDecision'
import InitializeDataDogClient from '@daedalus/core/src/analytics/components/DataDogClient'
import FullStoryLoader, {
  InjectFullStoryBodyClass
} from '@daedalus/core/src/analytics/components/FullStoryLoader'
import {InitializeCustomerIo} from '@daedalus/core/src/analytics/components/InitializeCustomerIo'
import TrackScreenshot from '@daedalus/core/src/analytics/components/TrackScreenshot'
import {TrackWebVitals} from '@daedalus/core/src/analytics/components/TrackWebVitals'
import {getPersistedVclidValue} from '@daedalus/core/src/analytics/utils/vclidCookie'
import {FetchUser} from '@daedalus/core/src/auth/components/FetchUser'
import SyncStateWithPrivilege from '@daedalus/core/src/auth/components/SyncStateWithPrivilege'
import TrackUserAuthentication from '@daedalus/core/src/auth/components/TrackUserAuthentication'
import {configureAmplify} from '@daedalus/core/src/auth/services'
import {
  checkIsBrandVio,
  getCheckoutHost,
  getMyBookingsHost
} from '@daedalus/core/src/brand/utils'
import {experimentVariationsStore} from '@daedalus/core/src/experiments/components/OpticksProvider'
import {getDataFile} from '@daedalus/core/src/experiments/modules/selectors'
import LanguageConfig from '@daedalus/core/src/localization/components/LanguageConfig'
import {GetBoVioConfiguration} from '@daedalus/core/src/offer/services/offerConfigurationApi/GetBoVioConfiguration'
import {urlHasParam} from '@daedalus/core/src/utils/url'
import {ProfileKey} from '@findhotel/sapi'

import {MIGRATED_ROUTER_PATHS} from './constants'
import {getSearchIdFromConditionalSource} from './modules/searchApi/selectors'
import {openSearchBox, setIsSearchBoxActive} from './modules/searchBox/slice'
import {imageProvider} from './utils/imageProvider'

configureAmplify()

interface Props {
  children: ReactNode
}

const NEXT_PATH_REGEXES = MIGRATED_ROUTER_PATHS.map(path => pathToRegexp(path))
const isNextRouter = (path: string) =>
  NEXT_PATH_REGEXES.some(regex => regex.test(path))

const isCrossingRouters = (currentPath: string, newPath: string) =>
  isNextRouter(currentPath) !== isNextRouter(newPath)

/**
 * Remove `react-router-dom` context in pages migrated to Next Pages router.
 * This will cause hooks like useHistory to crash in development.
 *
 * Also provide router abstraction for shared code
 *
 * TODO: Clean this up when all pages are migrated to Next.js - sc-147995
 * We should just keep <RouterProviderNext>
 */
const ReactRouterProvider = ({children}) => {
  const {pathname} = useRouter()
  const isNextRouter = MIGRATED_ROUTER_PATHS.includes(pathname)
  const disableReactRouter =
    process.env.NODE_ENV === 'development' && isNextRouter

  const childrenWithProvider = isNextRouter ? (
    <RouterProviderNext isCrossingRouters={isCrossingRouters}>
      {children}
    </RouterProviderNext>
  ) : (
    <RouterProvider isCrossingRouters={isCrossingRouters}>
      {children}
    </RouterProvider>
  )

  if (disableReactRouter) return childrenWithProvider
  return <Router history={history}>{childrenWithProvider}</Router>
}

// These are wrapped in a component to mount inside RouterProvider
// TODO: useSyncOverlayState could use the Next router directly and mount in AppProviders - sc-147995
const RouterHooks = () => {
  useSUSIBottomSheet()
  useSyncOverlayState()

  return null
}

/**
 * This component wraps all pages with the necessary context providers
 */
const AppProviders = ({children}: Props) => {
  const languageCode = useSelector(getLanguageCode)
  const {brand} = useBrand()
  const anonymousId = useSelector(getAnonymousId)
  const userCountryCode = useSelector(getUserCountryCode)
  const currencyCode = useSelector(getCurrencyCode)
  const deviceLayout = useSelector(getDeviceLayout)
  const isMobile = deviceLayout !== 'desktop'
  const isSyntheticTest = useSelector(getIsSyntheticTest)
  const {updateLocation} = useUrlUpdater()

  const appEnv = process.env.NEXT_PUBLIC_ENV
  const publicUrl = process.env.NEXT_PUBLIC_ASSETS_ENDPOINT
  const sapiProfileKey = process.env.NEXT_PUBLIC_SAPI_PROFILE_KEY as ProfileKey

  const boVioEndpoint = process.env.NEXT_PUBLIC_BOVIO_ENDPOINT
  const appVersion = process.env.NEXT_PUBLIC_APP_VERSION
  const checkoutUrl = getCheckoutHost({brand, appEnv})
  const bookingManagementUrl = getMyBookingsHost({
    brand,
    appEnv
  })

  const vclid = getQueryStringObject()?.vclid || getPersistedVclidValue()

  const oneTimePasswordUrl = `${process.env.NEXT_PUBLIC_MEMBERSHIP_API_ENDPOINT}auth/send-one-time-password${checkIsBrandVio(brand) ? '' : '-etrip'}`

  const verifyUrl = `${process.env.NEXT_PUBLIC_MEMBERSHIP_API_ENDPOINT}auth/verify-one-time-password${checkIsBrandVio(brand) ? '' : '-etrip'}`

  usePageLoadStartTimer()

  const datafile = useSelector(getDataFile)
  const optimizelyFlags = datafile?.featureFlags?.map(({key}) => key) || []

  useEffect(() => {
    if (
      urlHasParam({search: window.location.search, param: 'simulate'}) ||
      getPersistedSimulationFlag()
    ) {
      import('mocks/initializeSimulator').then(module => module.default())
    }
  }, [])

  return (
    <InitializeSentry>
      {/* Capture and store Vio Click Identifier */}
      <CaptureVioClickID />
      <TrackScreenshot action={setScreenshotTaken} />
      <TrackGoogleTranslateUsage />
      <InitializeDataDogClient
        clientToken={process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN}
        appVersion={appVersion}
        appEnv={appEnv}
        applicationId={process.env.NEXT_PUBLIC_DD_RUM_APP_ID}
        sampleRate={Number(process.env.NEXT_PUBLIC_DD_SAMPLE_RATE)}
        premiumSampleRate={Number(
          process.env.NEXT_PUBLIC_DD_REPLAY_SAMPLE_RATE
        )}
        service="search"
      />

      <ErrorBoundary>
        <ToastProvider>
          <DeviceLayoutProvider
            updateOnMount
            value={deviceTypeToBreakpoint(deviceLayout)}
          >
            <PerformanceMonitor />
            <TrackSSRExperiments />
            <TmsPanelProvider appEnv={appEnv} />

            <FullStoryLoader publicUrl={publicUrl} />
            <InjectFullStoryBodyClass />
            <InitializeCustomerIo />
            <SyncStateWithDeviceLayout />
            <LanguageConfig locale={languageCode} />

            <ReactRouterProvider>
              <RouterHooks />
              <TrackWebVitals />
              <SyncStateWithPrivilege />
              <SyncStateWithPriceWatchHoldoutGroup />

              <FetchUser publicUrl={publicUrl} />
              <PriceWatchTrackingsSync />
              <FetchAclDecision anonymousId={anonymousId} />
              <TrackUserAuthentication />
              <FontsLoader brand={brand} languageCode={languageCode} />
              <InjectPageHeadTags />
              <InjectPageMetaDescription />
              <SusiProvider
                brand={brand}
                userCountryCode={userCountryCode}
                anonymousId={anonymousId}
              >
                <DebugPanelProvider
                  isMobile={isMobile}
                  appEnv={appEnv}
                  isSyntheticTest={isSyntheticTest}
                  thirdPartyExperiments={optimizelyFlags}
                  meta={{anonymousId}}
                  variationsStoreReader={experimentVariationsStore}
                  scenarios={scenariosList}
                  modulesConfig={[
                    DebugPanelModules.EXPERIMENTS,
                    DebugPanelModules.SIMULATOR,
                    DebugPanelModules.SETTINGS,
                    DebugPanelModules.SORT
                  ]}
                  updateLocation={updateLocation}
                >
                  {children}
                </DebugPanelProvider>
                <PriceWatchOverlays />
                <PreferencesOverlay />
              </SusiProvider>
              <PasswordlessLogin
                brand={brand}
                languageCode={languageCode}
                anonymousId={anonymousId}
              />
              <GetBoVioConfiguration
                boVioUrl={boVioEndpoint}
                brandCode={brand.code}
                appEnv={appEnv}
                urlParams={getQueryStringObject()}
              />
              <DealFreeze
                imageProvider={imageProvider}
                appCurrency={currencyCode}
                sapiProfileKey={sapiProfileKey}
                application={ApplicationNames.Search}
                appVersion={appVersion}
                appEnv={appEnv}
                boVioEndpoint={boVioEndpoint}
                optimizelyFlags={optimizelyFlags}
                checkoutUrl={checkoutUrl}
                bookingManagementUrl={bookingManagementUrl}
                languageCode={languageCode}
                actions={{
                  openSearchBox,
                  setIsSearchBoxActive
                }}
                getSearchId={getSearchIdFromConditionalSource}
                getCountryCode={getUserCountryCode}
                vclid={vclid ? String(vclid) : undefined}
              />
              <OneTimePasswordFlow
                brand={brand}
                languageCode={languageCode}
                oneTimePasswordUrl={oneTimePasswordUrl}
                verifyUrl={verifyUrl}
                userCountryCode={userCountryCode}
              />
              <Web2AppPopup />
              <EmailSignUpProvider
                languageCode={languageCode}
                apiUrl={process.env.NEXT_PUBLIC_MEMBERSHIP_API_ENDPOINT}
              >
                <EmailFlow />
              </EmailSignUpProvider>
            </ReactRouterProvider>
          </DeviceLayoutProvider>
        </ToastProvider>
      </ErrorBoundary>
    </InitializeSentry>
  )
}

export default AppProviders
